import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, UserRound } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { ListCustomers } from '@cold-ambulance/client';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@cold-ambulance/shadcn';
import {
  ConfirmDialog,
  SearchInput,
  TableRowSort,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import {
  createdAtColumn,
  emailColumn,
  nameColumn,
  phoneNumberColumn,
} from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { RejectApplicantDialog } from '../../components/reject-applicant-dialog.tsx';
import {
  StickyTableBox,
  StickyTableHeader,
} from '../../components/sticky-table-box.tsx';
import { UpdateCustomerDialog } from './update-customer.tsx';
import { ViewCustomerDocumentsDialog } from './view-customer-documents-dialog.tsx';

type CustomerRecord = ListCustomers['records'][number];

const columns: ColumnDef<CustomerRecord>[] = [
  nameColumn(),
  emailColumn(),
  phoneNumberColumn((row) => row.contact),
  {
    accessorKey: 'approved',
    header: 'Status',
    cell: ({ row }) => {
      const approved = row.original.approved;
      if (approved === true) {
        return <Badge variant="default">Approved</Badge>;
      }
      if (approved === false) {
        return <Badge variant="destructive">Rejected</Badge>;
      }
      return <Badge variant="secondary">Pending</Badge>;
    },
  },
  createdAtColumn(),
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => <CustomerActionsCell customer={row.original} />,
  },
];

export default function CustomersPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /customers',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      query: searchQuery || undefined,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as 'name' | 'email' | 'createdAt' | 'approved')
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Customers"
        totalCount={`${dataQuery.data?.totalCount || 0} total customers`}
        icon={UserRound}
      />

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}

function CustomerActionsCell({ customer }: { customer: CustomerRecord }) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  const deleteMutation = useAction('DELETE /customer/{id}', {
    invalidate: ['GET /customers'],
    meta: { toast: true },
    onSuccess: () => toast.success('Customer deleted'),
  });
  const approveMutation = useAction('PATCH /customer/{id}/approve', {
    invalidate: ['GET /customers'],
    meta: { toast: true },
    onSuccess: () => toast.success('Customer approved'),
  });
  const rejectMutation = useAction('PATCH /customer/{id}/reject', {
    invalidate: ['GET /customers'],
    meta: { toast: true },
    onSuccess: () => toast.success('Customer rejected'),
  });
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <UpdateCustomerDialog
            customer={customer}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                Edit customer
              </DropdownMenuItem>
            }
          />
          <ViewCustomerDocumentsDialog customerId={customer.id} />
          {customer.approved !== true && (
            <DropdownMenuItem
              disabled={approveMutation.isPending}
              onSelect={(e) => {
                e.preventDefault();
                approveMutation.mutate({ id: customer.id });
              }}
            >
              {approveMutation.isPending
                ? 'Approving...'
                : 'Approve customer'}
            </DropdownMenuItem>
          )}
          {customer.approved === null && (
            <RejectApplicantDialog
              applicantId={customer.id}
              applicantName={customer.name}
              role="customer"
              mutation={rejectMutation}
            />
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={(e) => {
              e.preventDefault();
              setDeleteOpen(true);
            }}
          >
            Delete customer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        mutation={{
          mutate: () => deleteMutation.mutate({ id: customer.id }),
          isPending: deleteMutation.isPending,
        }}
        title="Delete customer"
        description={
          <span>
            Are you sure you want to delete the customer{' '}
            <strong>{customer.name}</strong>? This action cannot be undone.
          </span>
        }
      />
    </>
  );
}
